import type { HubMetricTarget } from "@expert/app-config";

import type { MetricTone } from "./targetProgress";

import {
    computeDirectProgress,
    computeInverseProgress,
    computeMetricStatus,
    computeMetricTone,
} from "./targetProgress";

/** Formats a metric value for display. Ratings and percents use 1 fraction digit; counts use 0. */
export function formatValue(value: number, decimals = 0): string {
    return value.toFixed(decimals);
}

/** Trailing unit glyph. Only percents carry one — ratings ("4.8") and counts ("10") render bare. */
export function unitSuffix(unit: HubMetricTarget["unit"]): string {
    return unit === "percent" ? "%" : "";
}

/**
 * Target text beneath the value, which the card prefixes with "Target: ":
 * - count ⇒ "10 sales" / "1 sale" (noun pluralized by the target value);
 * - otherwise ⇒ "95% or higher" / "12% or lower" (unit suffix + comparison word).
 * Null when the card has no target, so no footer renders.
 */
export function buildFooter(target?: HubMetricTarget): string | null {
    if (!target) return null;
    if (target.unit === "count") {
        const label = target.noun ? (target.value === 1 ? target.noun : `${target.noun}s`) : "";
        return label ? `${target.value} ${label}` : `${target.value}`;
    }
    return `${target.value}${unitSuffix(target.unit)} or ${target.comparison}`;
}

export interface MetricGrade {
    status: string;
    tone: MetricTone;
}

/** Status label + tone for a value against its target. "lower is better" targets (transfer rate) score
 *  on the inverse-progress curve; everything else on direct progress. */
export function resolveTone(value: number, target: HubMetricTarget): MetricGrade {
    const progress =
        target.comparison === "lower"
            ? computeInverseProgress({ actual: value, target: target.value })
            : computeDirectProgress(value, target.value);
    return { status: computeMetricStatus(progress), tone: computeMetricTone(progress) };
}
